import React, { useEffect, useState } from "react";
import {
  ArrowPathIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  MinusIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
} from "@heroicons/react/20/solid";
import { Role, RoleStatus } from "../../../Models";
import { Disclosure } from "@headlessui/react";
import ReactDatePicker from 'react-datepicker';
import { getAllRoles } from "../../../api/candidates.api";


interface FilterSection {
  name: string;
  options: Options[];
}
interface Options {
  id: string;
  name: string;
}
interface TestLeftSidebarProps {
  title?: string;
  onToggle?: (isOpen: boolean) => void;
  handleFilterQuery: (filterQuery: string) => void;
  handleSearchQuery: (searchQuery: string) => void;
  handleDateFilter: (dateFilter: string) => void;
}



const TestLeftSidebar: React.FC<TestLeftSidebarProps> = ({
  handleFilterQuery,
  onToggle,
  handleSearchQuery,
  handleDateFilter
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(true);
  const [roles, setRoles] = useState<Role[]>([]);
  const [status, setStatus] = useState<RoleStatus[]>([]);
  const [selectedFilters, setSelectedFilters] = useState<
    Record<string, string[]>
  >({
    Roles: ["all"],
    Status: ["all"],
    Date: ["all"],
  });
  const [searchQuery, setSearchQuery] = useState<string>("");

 
  useEffect(() => {
    handleSearchQuery(searchQuery);
  }, [searchQuery]);


  // Function to call getAllRoles webcall
  useEffect(() => {
       fetchAllRoles();
  }, []);

  const fetchAllRoles = async () => {
    try {
      const allRoles = await getAllRoles();
      setRoles(allRoles);
    } catch (error) {
      console.error("Failed to fetch all roles:", error);
    }
  };

  const handleFilterChange = (sectionName: string, id: string , optionName : string) => {
    setSelectedFilters((prev) => {
      if (id === "all") {
        return {
          ...prev,
          [sectionName]: ["all"],
        };
      }
      const old = prev[sectionName] || [];
      // Remove "all" when selecting a real value
      let next = old.filter((opt) => opt !== "all");
      if (next.includes(optionName)) {
        next = next.filter((opt) => opt !== optionName);
      } else {
        // Select option name 
        if(sectionName == "Roles")
            next = [...next, optionName];
        else next = [optionName]
    }
      // If nothing is selected, revert to "all"
      if (next.length === 0) {
        next = ["all"];
      }
      return {
        ...prev,
        [sectionName]: next,
      };
    });
  };

  
  const handleResetFilters = () => {
    setSelectedFilters({
      Roles: ["all"],
      Status: ["all"],
      Date: ["all"],
    });
  };

  const toggleSidebar = () => {
    setIsOpen((prev) => {
      const next = !prev;
      onToggle?.(next);
      return next;
    });
  };
  // Build filter query string from selectedFilters (excluding DateTime)
  useEffect(() => {
    const query: string[] = [];

    // Roles filter
    if (
      selectedFilters.Roles &&
      selectedFilters.Roles.length &&
      !selectedFilters.Roles.includes("all")
    ) {
      const roleQueries = selectedFilters.Roles.map(
        (role) => `RoleName eq "${role}"`
      );
      query.push(`(${roleQueries.join(" or ")})`);
    }

    // Status filter
    if (
      selectedFilters.Status &&
      selectedFilters.Status.length &&
      !selectedFilters.Status.includes("all")
    ) {
      const statusValue = selectedFilters.Status[0];
      if (statusValue === "Active") {
        query.push("RoleStatus eq true");
      } else if (statusValue === "Inactive") {
        query.push("RoleStatus eq false");
      }
    }

    const filterQueryString = query.join(" and ");
    handleFilterQuery(filterQueryString);

    // Date filter - pass separately
    if (
      selectedFilters.Date &&
      selectedFilters.Date.length &&
      !selectedFilters.Date.includes("all")
    ) {
      const dateValue = selectedFilters.Date[0];
      // Map frontend values to backend expected values
      if (dateValue === "Today") {
        handleDateFilter("Today");
      } else if (dateValue === "Upcoming") {
        handleDateFilter("Upcoming");
      } else if (dateValue === "completed") {
        handleDateFilter("Completed");
      }
    } else {
      handleDateFilter("");
    }
  }, [selectedFilters]);


  const filterSections: FilterSection[] = [
    {
      name: "Roles",
      options: [
          {id:"all" , name: "All"},
          ...(roles || []).map(role => ({ id: role.id, name: role.title })),
      ],
    },
    {
      name: "Status",
      options: [
        { id: "all", name: "All" },
        { id: "Active", name: "Active" },
        { id: "Inactive", name: "Inactive" },
        { id: "Cancelled", name: "Cancelled" }
      ],
    },
    {
      name: "Date",
      options: [
        { id: "all", name: "All" },
        { id: "today", name: "Today" },
        { id: "Upcoming", name: "Upcoming" },
        { id: "completed", name: "Completed" }
      ],
    },
  ];
  return (
    <aside
      className={`select-none fixed z-10 top-[4.1rem] h-[calc(99vh-4rem)] left-0 shadow-md border dark:border-gray-500 transition-transform transform duration-200 w-[19rem] bg-white dark:bg-darkbg ${isOpen ? "translate-x-0" : "-translate-x-full"}`}
    >
      <div className="mx-auto bg-gray-100 dark:bg-darkbglight">
        <div className="min-w-0 px-4 pt-2 pb-1">
          <div className="text-gray-900 dark:text-gray-300 flex items-center space-x-2 justify-center">
            <h3 className="text-center text-lg font-semibold leading-7 sm:text-base sm:tracking-tight">
              Filters
            </h3>
          </div>
        </div>
      </div>

      <div
        className={`absolute top-10 ${isOpen ? "-right-2 shadow-md" : "-right-4 shadow-xl"} h-16 border border-gray-300 rounded-md flex items-center justify-center cursor-pointer bg-white dark:bg-darkbglight dark:border-gray-500 dark:text-gray-300`}
        onClick={toggleSidebar}
      >
        {isOpen ? (
          <ChevronDoubleLeftIcon width={16} />
        ) : (
          <ChevronDoubleRightIcon width={16} />
        )}
      </div>

      <div className="pb-12 h-full thin-scroll overflow-y-auto w-full flex flex-col items-center mt-2 px-4 text-gray-700 dark:text-gray-200">
        {/* addding search bar here */}
        <div className="relative flex focus-within:z-10 mt-2 items-center justify-between w-[90%] mb-4">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2">
            <MagnifyingGlassIcon
              className="h-5 w-5 text-gray-400 dark:text-gray-300"
              aria-hidden="true"
            />
          </div>
          <input
            type="text"
            name="search"
            id="search"
            className="block w-10/12 border-0 pl-9 text-gray-900 ring-1 ring-inset ring-gray-300 dark:ring-gray-500 placeholder:text-gray-400 focus:ring-1 focus:ring-inset focus:ring-entntorange sm:text-sm bg-transparent dark:text-gray-300 dark:placeholder:text-gray-400 rounded-md"
            placeholder="Quick Search"
            onChange={(e) => {
              setSearchQuery(e.target.value);
              
            }}
            value={searchQuery}
          />
          <div
            className="text-gray-400 hover:text-gray-500 cursor-pointer relative dark:hover:text-gray-300"
            onClick={handleResetFilters}
          >
            <FunnelIcon width={24} />
            <ArrowPathIcon width={14} className="absolute bottom-0 -right-1" />
          </div>
        </div>
        {/* mapping the filter sections */}
        {filterSections.map((section, index: number) => (
          <Disclosure
            as="div"
            key={index}
            className="w-[90%] border-b border-gray-200 dark:border-gray-500"
          >
            {({ open }) => (
              <>
                <Disclosure.Button className="flex w-full justify-between py-3 text-sm text-gray-400 hover:text-gray-500 dark:text-gray-300 font-semibold">
                  <span className="text-gray-900 dark:text-gray-400">
                    {section.name.split("_").join(" ")}
                  </span>
                  {open ? (
                    <MinusIcon className="h-5 w-5 text-gray-200" />
                  ) : (
                    <PlusIcon className="h-5 w-5 text-gray-200" />
                  )}
                </Disclosure.Button>
                <Disclosure.Panel className="pb-2 text-sm text-gray-500 dark:text-gray-400">
                  {section.options.map((option) => (
                    <div key={option.id} className="flex items-center mb-2">
                      <input
                        id={`${section.name}-${option.id}`}
                        name={`${section.name}[]`}
                        type="checkbox"
                        checked={selectedFilters[section.name].includes(
                          option.name== 'All' ?'all':option.name
                        )}
                        onChange={() =>
                          handleFilterChange(section.name, option.id , option.name)
                        }
                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <label
                        htmlFor={`${section.name}-${option.id}`}
                        className="ml-3 text-sm text-gray-600 dark:text-gray-400"
                      >
                        {option.name}
                      </label>
                    </div>
                  ))}
                </Disclosure.Panel>
              </>
            )}
          </Disclosure>
        ))}
      
      </div>
    </aside>
  );
};

export default TestLeftSidebar;
